import React, { ReactElement, ReactNode, useRef, useEffect, useMemo } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '@/common/lib/cn/cn'
import './table.css'

type AllProps = {
    className?: string
}

export type TableProps = {
    children:
        | ReactElement<TheadProps>
        | ReactElement<TbodyProps>
        | (ReactElement<TheadProps> | ReactElement<TbodyProps>)[]
    size?: 'xs' | 'md' | 'lg'
    pinCols?: boolean
    pinRows?: boolean
    striped?: boolean
    thBg?: boolean
    border?: boolean
    rounded?: boolean
} & Omit<React.HTMLAttributes<HTMLTableElement>, 'size'> &
    AllProps

export type TheadProps = {
    children: ReactElement<TrProps> | ReactElement<TrProps>[]
} & React.HTMLAttributes<HTMLTableSectionElement> &
    AllProps

export type TbodyProps = {
    children: ReactElement<TrProps> | ReactElement<TrProps>[]
} & React.HTMLAttributes<HTMLTableSectionElement> &
    AllProps

export type ThProps = {
    children?: ReactNode
} & React.HTMLAttributes<HTMLTableCellElement> &
    AllProps

export type TrProps = {
    children: ReactElement<TdProps> | ReactElement<ThProps> | (ReactElement<TdProps> | ReactElement<ThProps>)[]
} & React.HTMLAttributes<HTMLTableRowElement> &
    AllProps

export type TdProps = {
    children: ReactNode
    colSpan?: number
    rowSpan?: number
} & React.HTMLAttributes<HTMLTableCellElement> &
    AllProps

export const Table = ({
    children,
    className,
    size = 'md',
    pinCols,
    pinRows,
    striped,
    thBg,
    border,
    rounded = true,
    ...props
}: TableProps) => {
    const TableClass = cn(
        'table',
        {
            'table-xs': size === 'xs',
            'table-md': size === 'md',
            'table-lg': size === 'lg',
        },
        {
            'table-pin-cols': pinCols,
            'table-pin-rows': pinRows,
            'table-stripped': striped,
            'table-th-bg': thBg,
            'table-border': border,
            'table-rounded': rounded,
        },
        className
    )

    // const OverflowClass = cn(
    //     {
    //         'overflow-x-auto': pinCols,
    //         'overflow-y-auto': pinRows,
    //     },
    //     className
    // )
    //<div className={OverflowClass}></div>
    return (
        <table className={TableClass} {...props}>
            {children}
        </table>
    )
}

export const Thead = ({ children, className, ...props }: TheadProps) => {
    return (
        <thead className={cn('thead', className)} {...props}>
            {children}
        </thead>
    )
}

export const Tbody = ({ children, className, ...props }: TbodyProps) => {
    return (
        <tbody className={cn('tbody', className)} {...props}>
            {children}
        </tbody>
    )
}

export const Tr = ({ children, className, ...props }: TrProps) => {
    return (
        <tr className={cn('tr', className)} {...props}>
            {children}
        </tr>
    )
}

export const Td = ({ children, className, ...props }: TdProps) => {
    return (
        <td className={cn('td', className)} {...props}>
            {children}
        </td>
    )
}

export const Th = ({ children, className, ...props }: ThProps) => {
    return (
        <th className={cn('th', className)} {...props}>
            {children}
        </th>
    )
}

const TABLE_SCROLL_HOST_MIN_HEIGHT_CLASS = {
    '8rem': 'min-h-[8rem]',
    '10rem': 'min-h-[10rem]',
    '14rem': 'min-h-[14rem]',
} as const

export type TableScrollHostMinHeight = keyof typeof TABLE_SCROLL_HOST_MIN_HEIGHT_CLASS

export type TableScrollHostProps = {
    children: ReactNode
    className?: string
    minHeight?: TableScrollHostMinHeight | false
    /** Рамка на scroll-обёртке (для Table с rounded={false}) */
    outline?: boolean
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>

export const TableScrollHost = ({
    children,
    className,
    minHeight = '14rem',
    outline = true,
    ...props
}: TableScrollHostProps) => {
    return (
        <div
            className={cn(
                'table-scroll-host relative overflow-x-auto',
                outline && 'table-overflow-rounded table-border',
                minHeight !== false && TABLE_SCROLL_HOST_MIN_HEIGHT_CLASS[minHeight],
                className
            )}
            {...props}
        >
            {children}
        </div>
    )
}

export type TableCellTextProps = {
    children: ReactNode
    lines?: number
    className?: string
} & React.HTMLAttributes<HTMLDivElement>

export const TableCellText = ({ children, lines = 2, className, style, ...props }: TableCellTextProps) => {
    return (
        <div
            className={cn('table-cell-text', className)}
            style={
                {
                    display: '-webkit-box',
                    WebkitLineClamp: lines,
                    WebkitBoxOrient: 'vertical',
                    ...style,
                } as React.CSSProperties
            }
            {...props}
        >
            {children}
        </div>
    )
}

const buildTableSearchHighlightParts = (
    text: string | null | undefined,
    highlight: string | null | undefined
): ReactNode => {
    const haystack = text == null ? '' : String(text)
    const needle = (highlight ?? '').trim()
    if (!needle) return haystack

    const lowerText = haystack.toLowerCase()
    const lowerNeedle = needle.toLowerCase()
    const nodes: ReactNode[] = []
    let start = 0
    let found = lowerText.indexOf(lowerNeedle, start)
    let partKey = 0

    while (found !== -1) {
        if (found > start) {
            nodes.push(haystack.slice(start, found))
        }
        nodes.push(
            <mark
                key={`h-${partKey++}`}
                className='rounded px-0.5 bg-[rgba(190,150,255,0.35)] text-inherit [box-decoration-break:clone]'
            >
                {haystack.slice(found, found + needle.length)}
            </mark>
        )
        start = found + needle.length
        found = lowerText.indexOf(lowerNeedle, start)
    }

    if (start < haystack.length) {
        nodes.push(haystack.slice(start))
    }

    if (nodes.length === 0) return haystack
    return <>{nodes}</>
}

export type TableCellSearchHighlightProps = {
    text: string | null | undefined
    searchHighlight?: string | null
} & Omit<TableCellTextProps, 'children'>

export const TableCellSearchHighlight = ({
    text,
    searchHighlight,
    lines = 2,
    className,
    ...props
}: TableCellSearchHighlightProps) => {
    const content = useMemo(() => buildTableSearchHighlightParts(text, searchHighlight), [text, searchHighlight])

    return (
        <TableCellText lines={lines} className={className} {...props}>
            {content}
        </TableCellText>
    )
}

export type TableVirtualProps = {
    rowCount: number
    rowHeight: number
    renderRow: (index: number) => ReactElement<TrProps>
    height?: number
    overscan?: number
    rounded?: boolean
    children: ReactElement<TheadProps>
    className?: string
    onVisibleIndicesChange?: (indices: number[], scrollIndex: number) => void
    scrollToIndex?: number
} & Pick<TableProps, 'rounded' | 'className' | 'striped' | 'thBg' | 'pinRows'>

export const TableVirtual = ({
    rowCount,
    rowHeight,
    renderRow,
    height = 400,
    overscan = 5,
    rounded = true,
    className,
    children,
    onVisibleIndicesChange,
    scrollToIndex,
    ...props
}: TableVirtualProps) => {
    const parentRef = useRef<HTMLDivElement>(null)
    const onVisibleIndicesChangeRef = useRef(onVisibleIndicesChange)
    onVisibleIndicesChangeRef.current = onVisibleIndicesChange
    const hasRestoredScroll = useRef(false)

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => rowHeight,
        overscan,
    })

    useEffect(() => {
        if (scrollToIndex == null || scrollToIndex < 0 || rowCount === 0 || !parentRef.current) return
        if (scrollToIndex === 0) {
            hasRestoredScroll.current = false
            virtualizer.scrollToIndex(0, { align: 'start' })
            return
        }
        if (hasRestoredScroll.current) return
        hasRestoredScroll.current = true
        virtualizer.scrollToIndex(Math.min(scrollToIndex, rowCount - 1), { align: 'start' })
    }, [scrollToIndex, rowCount, virtualizer])
    const virtualItems = virtualizer.getVirtualItems()
    const totalSize = virtualizer.getTotalSize()

    const visibleIndicesKey = virtualItems.map(v => v.index).join(',')

    useEffect(() => {
        if (!onVisibleIndicesChangeRef.current) return
        const indices = visibleIndicesKey ? visibleIndicesKey.split(',').map(Number) : []
        const indexFromScroll = parentRef.current
            ? Math.floor(parentRef.current.scrollTop / rowHeight)
            : (indices[0] ?? 0)
        onVisibleIndicesChangeRef.current(indices, indexFromScroll)
    }, [visibleIndicesKey, rowHeight])
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
    const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0
    const bodyRows: ReactElement<TrProps>[] = []

    const thead = React.Children.only(children) as ReactElement<TheadProps>
    const tr = thead?.props?.children
    const columns = React.Children.toArray(tr)
        .filter((c): c is ReactElement<ThProps> => React.isValidElement(c) && c.type === Th)
        .map(th => th.props.children)

    if (paddingTop > 0) {
        bodyRows.push(
            <Tr key='virtual-top-spacer' aria-hidden='true'>
                <Td colSpan={columns.length} style={{ height: `${paddingTop}px`, padding: 0, border: 0 }}>
                    {' '}
                </Td>
            </Tr>
        )
    }

    const { striped: isStriped } = props

    bodyRows.push(
        ...virtualItems.map(virtualRow => {
            const row = renderRow(virtualRow.index)
            const stripeByDataIndex = isStriped === true && virtualRow.index % 2 === 1
            return React.cloneElement(row, {
                key: virtualRow.key,
                className: cn(row.props.className, stripeByDataIndex && 'tr-row-striped'),
                style: {
                    ...row.props.style,
                    height: `${rowHeight}px`,
                    minHeight: `${rowHeight}px`,
                    maxHeight: `${rowHeight}px`,
                },
            })
        })
    )

    if (paddingBottom > 0) {
        bodyRows.push(
            <Tr key='virtual-bottom-spacer' aria-hidden='true'>
                <Td colSpan={columns.length} style={{ height: `${paddingBottom}px`, padding: 0, border: 0 }}>
                    {' '}
                </Td>
            </Tr>
        )
    }

    const overflowClass = cn(
        'overflow-y-auto w-full table-virtual-scroll',
        { 'table-overflow-rounded': rounded },
        className
    )
    return (
        <div
            ref={parentRef}
            className={overflowClass}
            style={{ height: `${height}px`, ['--table-virtual-row-height']: `${rowHeight}px` } as React.CSSProperties}
        >
            <Table rounded={false} className='table-virtual' {...props}>
                <>
                    {children}
                    <Tbody>{bodyRows}</Tbody>
                </>
            </Table>
        </div>
    )
}
